import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { QuoteItemResponse, QuoteSignatureView } from '../../models/quote.model';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import { QuoteDocumentData } from './quote-document.model';

/**
 * Componente PRESENTACIONAL del documento legal de una cotización con el formato
 * del modelo cotizacion.html (header con logo + COTIZACIÓN + número/meta + badge,
 * partes Proveedor|Cliente, tabla de 9 columnas zebra, totales, términos+cláusulas,
 * doble firma y pie legal con hash).
 *
 * Se alimenta de un único @Input() `data` normalizado (QuoteDocumentData) para ser
 * agnóstico al origen — lo usan tanto el portal público como el detalle interno del CRM.
 * Construye el `data` con quoteDocumentFromPublic() / quoteDocumentFromResponse().
 */
@Component({
  selector: 'app-quote-document',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './quote-document.component.html',
})
export class QuoteDocumentComponent {
  @Input({ required: true }) data!: QuoteDocumentData;

  constructor(private datetime: DatetimeService) {}

  formatFecha(iso?: string | null): string {
    return iso ? this.datetime.formatDate(iso) : '—';
  }

  formatFechaHora(iso?: string | null): string {
    return iso ? this.datetime.formatDateTimeShort(iso) : '—';
  }

  /** true si la firma es una imagen (drawn/uploaded → data URL). */
  isImageSignature(sig: QuoteSignatureView | null): boolean {
    return !!sig?.signatureData && /^data:image\//i.test(sig.signatureData);
  }

  /**
   * Impuesto a mostrar por línea (V75), como texto "%": usa el itemTaxRate de la línea si tiene;
   * si no, cae al taxRate de la cotización (ya convertido a % en data.taxRatePercent); si no hay
   * ninguno, "—". itemTaxRate se persiste como fracción (0.18) igual que taxRate, así que ×100.
   */
  itemTaxLabel(item: QuoteItemResponse): string {
    const raw = item.itemTaxRate;
    if (raw != null && raw !== 0) {
      return `${this.round2(raw * 100)}%`;
    }
    if (this.data.taxEnabled && this.data.taxRatePercent) {
      return `${this.round2(this.data.taxRatePercent)}%`;
    }
    return '—';
  }

  private round2(n: number): number {
    return Math.round(n * 100) / 100;
  }

  /** true si la columna Código debe mostrarse (default true cuando no se especifica). */
  get showCodes(): boolean {
    return this.data.showItemCodes !== false;
  }

  /** Número de columnas de la tabla, para calcular el colspan de los subencabezados de categoría. */
  get columnCount(): number {
    // # , [Código], Producto, Descripción, Cantidad, Unidad, Precio, Descuento, Impuesto, Total
    return this.showCodes ? 10 : 9;
  }

  /**
   * Ítems agrupados por categoría (V76), preservando el orden de aparición de cada categoría.
   * Los ítems sin categoría quedan en el grupo "Sin categoría" SIEMPRE al final.
   * Solo se usa cuando data.groupByCategory === true.
   */
  get itemGroups(): { category: string; items: QuoteItemResponse[] }[] {
    const NO_CAT = 'Sin categoría';
    const order: string[] = [];
    const map = new Map<string, QuoteItemResponse[]>();
    for (const item of this.data.items || []) {
      const cat = (item.category && item.category.trim()) ? item.category.trim() : NO_CAT;
      if (!map.has(cat)) {
        map.set(cat, []);
        order.push(cat);
      }
      map.get(cat)!.push(item);
    }
    // Mover "Sin categoría" al final si existe.
    const sorted = order.filter(c => c !== NO_CAT);
    if (map.has(NO_CAT)) sorted.push(NO_CAT);
    return sorted.map(category => ({ category, items: map.get(category)! }));
  }

  /**
   * (Incidente 14) true si AL MENOS UN ítem tiene una categoría real definida. Si ningún ítem tiene
   * categoría, el agrupado por categorías se omite (no se muestra un único grupo "Sin categoría") y
   * el documento cae a la tabla plana.
   */
  get hasRealCategories(): boolean {
    return (this.data.items || []).some(it => !!(it.category && it.category.trim()));
  }

  /** true si la tabla debe renderizarse agrupada por categoría: solo si se pidió Y hay categorías reales. */
  get showGroupedByCategory(): boolean {
    return !!this.data.groupByCategory && this.hasRealCategories;
  }

  // ------------------------------------------------------------
  // (Incidente 15) Desglose de pago a crédito — calculado en el frontend/PDF.
  // No altera subtotal/tax/total ni los ítems: es solo un desglose informativo.
  //   anticipo               = total × advancePercent / 100   (advancePercent null → 0)
  //   saldo (financiado)     = total − anticipo
  //   interes                = saldo × interestRatePercent / 100
  //   totalConFinanciamiento = total + interes
  // El desglose de financiamiento (saldo/intereses/total con financiamiento) se muestra solo
  // cuando el pago es a CRÉDITO y hay tasa de interés (> 0) y total. Redondeo HALF_UP a 2 decimales.
  // ------------------------------------------------------------

  /**
   * (INC_CRM_005 (5) inc43) true si hay anticipo (> 0) sobre un total conocido, sea cual sea la
   * forma de pago. El desglose en dinero solo existía dentro del bloque de crédito, así que una
   * cotización al contado mostraba "Anticipo 10%" sin monto ni saldo pendiente.
   */
  get showAdvanceBreakdown(): boolean {
    const d = this.data;
    return d.advancePercent != null && d.advancePercent > 0 && d.totalAmount > 0;
  }

  /** true si la cotización es a crédito con anticipo y plazo definidos (bloque base de crédito). */
  get showCreditBreakdown(): boolean {
    const d = this.data;
    return d.paymentType === 'CREDITO' && d.advancePercent != null && d.creditTermDays != null;
  }

  /** Título del bloque de desglose: solo habla de crédito cuando el pago realmente lo es. */
  get breakdownTitle(): string {
    return this.data.paymentType === 'CREDITO' ? 'Desglose de pago a crédito' : 'Desglose de pago';
  }

  /**
   * true si debe mostrarse el desglose de financiamiento: pago a CRÉDITO con tasa de interés
   * definida (> 0) y un total mayor a cero. Independiente de que haya anticipo/plazo.
   */
  get showFinancingBreakdown(): boolean {
    const d = this.data;
    return d.paymentType === 'CREDITO'
      && d.interestRatePercent != null && d.interestRatePercent > 0
      && d.totalAmount > 0;
  }

  /** Monto del anticipo = total × advancePercent / 100 (advancePercent null se trata como 0). */
  get advanceAmount(): number {
    const d = this.data;
    return d.advancePercent != null ? d.totalAmount * (d.advancePercent / 100) : 0;
  }

  /** Saldo restante / financiado = total − anticipo. */
  get remainingBalance(): number {
    return this.data.totalAmount - this.advanceAmount;
  }

  /** Saldo financiado (redondeado) = total − anticipo. */
  get financedBalance(): number {
    return this.round2(this.remainingBalance);
  }

  /** Intereses = saldo financiado × interestRatePercent / 100 (HALF_UP a 2 decimales). */
  get interestAmount(): number {
    const rate = this.data.interestRatePercent ?? 0;
    return this.round2(this.remainingBalance * (rate / 100));
  }

  /** Total con financiamiento = total + intereses (HALF_UP a 2 decimales). */
  get totalWithFinancing(): number {
    return this.round2(this.data.totalAmount + this.interestAmount);
  }

  /** Total final ajustado por tipo de cambio (valor convertido). null si no aplica exchangeRate. */
  get convertedTotal(): number | null {
    const d = this.data;
    if (d.exchangeRate == null || d.exchangeRate <= 0) return null;
    return d.totalAmount * d.exchangeRate;
  }

  /**
   * (V188) Rótulo del total convertido. Nombra la moneda de conversión configurada
   * ("Total en Dólar Estadounidense (USD)") y solo cae al rótulo con la tasa
   * ("Total (T.C. 0.2968)") cuando no hay moneda que nombrar: tasa tecleada a mano
   * o cotización anterior a V188.
   */
  get convertedTotalLabel(): string {
    const d = this.data;
    const name = d.exchangeCurrencyName?.trim() || null;
    const code = d.exchangeCurrencyCode?.trim() || null;
    if (name && code) return `Total en ${name} (${code})`;
    if (name) return `Total en ${name}`;
    if (code) return `Total en ${code}`;
    return `Total (T.C. ${d.exchangeRate})`;
  }

  /** Símbolo de la moneda de conversión, con espacio. Vacío si la tasa fue manual. */
  get convertedTotalSymbol(): string {
    const symbol = this.data.exchangeCurrencySymbol?.trim();
    return symbol ? `${symbol} ` : '';
  }

  /** true si hay al menos una condición comercial con valor (para decidir si renderizar el bloque). */
  get hasCommercialConditions(): boolean {
    const d = this.data;
    return !!(d.paymentType || d.advancePercent != null || d.exchangeRate != null
      || d.deliveryTime || d.warranty || d.deliveryPlace);
  }

  /**
   * Etiqueta de la forma de pago: usa paymentTypeLabel si vino; si no, la deriva del enum.
   * Para crédito agrega "a N días" cuando hay plazo.
   */
  get paymentLabel(): string {
    const d = this.data;
    if (!d.paymentType) return '';
    const base = d.paymentTypeLabel || (d.paymentType === 'CONTADO' ? 'Contado' : 'Crédito');
    if (d.paymentType === 'CREDITO' && d.creditTermDays != null) {
      return `${base} a ${d.creditTermDays} días`;
    }
    return base;
  }
}
